import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { insertUserSchema } from '@shared/schema';
import { useLocation, useParams } from 'wouter';
import * as z from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { ThemeToggle } from '@/components/theme-toggle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoCircledIcon } from '@radix-ui/react-icons';

// Define schema for login form validation
const loginSchema = z.object({
  username: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

// Define a custom schema for user registration with domelec.co.uk domain validation
const registerSchema = z.object({
  username: z
    .string()
    .email({ message: 'Please enter a valid email address' })
    .refine((email) => email.toLowerCase().endsWith('@domelec.co.uk'), {
      message: 'Only emails from domelec.co.uk domain are allowed to register',
    }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

// Define schema for reset password form validation
const resetPasswordSchema = z
  .object({
    password: z.string().min(6, {
      message: 'Password must be at least 6 characters',
    }),
    confirmPassword: z.string().min(6, {
      message: 'Password must be at least 6 characters',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export default function AuthPage() {
  const [, setLocation] = useLocation();
  const params = useParams();
  const {
    user,
    loginMutation,
    registerMutation,
    resetPasswordMutation,
    resendVerificationMutation,
  } = useAuth();
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [sendingForgotPasswordEmail, setSendingForgotPasswordEmail] = useState(false);
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [showPasswordResetSuccess, setShowPasswordResetSuccess] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationError, setVerificationError] = useState<any>(null);

  // Setup form for login
  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Setup form for registration with domelec.co.uk validation
  const registerForm = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Setup form for password reset
  const resetPasswordForm = useForm<z.infer<typeof resetPasswordSchema>>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Check for reset token in URL path
  useEffect(() => {
    if (params.token) {
      setResetToken(params.token);
      setShowResetForm(true);
    }
  }, [params.token]);

  // Handle forgot password form submission
  const handleForgotPassword = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!forgotPasswordEmail || forgotPasswordEmail.trim() === '') {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address',
        variant: 'destructive',
      });
      return;
    }

    setSendingForgotPasswordEmail(true);
    try {
      const response = await fetch('/api/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: forgotPasswordEmail }),
      });

      if (response.ok) {
        // Show success view instead of just toast
        setShowPasswordResetSuccess(true);
        setShowForgotPasswordForm(false);

        // Auto-redirect with countdown
        let countdown = 4;
        const timer = setInterval(() => {
          countdown--;
          const countdownElement = document.getElementById('countdown');
          if (countdownElement) {
            countdownElement.textContent = countdown.toString();
          }

          if (countdown <= 0) {
            clearInterval(timer);
            setShowPasswordResetSuccess(false);
          }
        }, 1000);
      } else {
        const data = await response.json();
        toast({
          title: 'Error',
          description: data.message || 'Failed to send reset email. Try again later.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingForgotPasswordEmail(false);
    }
  };

  // Redirect if user is already logged in
  if (user) {
    return null;
  }

  // Render password reset form if token is available
  if (showResetForm && resetToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-0">
        <div className="w-full relative">
          {/* Top wave design - decorative element */}
          <div className="absolute top-0 inset-x-0 h-16 md:h-20 lg:h-32 bg-indigo-600 dark:bg-indigo-700 opacity-60 dark:opacity-30 overflow-hidden">
            <svg
              className="absolute bottom-0 w-full text-white dark:text-slate-900"
              viewBox="0 0 1440 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
            >
              <path
                d="M0 48H1440V0C1440 0 1082.89 40 720 40C357.11 40 0 0 0 0V48Z"
                fill="currentColor"
              />
            </svg>
          </div>

          {/* Main content container */}
          <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 z-10 relative">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                {/* Logo container */}
                <div className="flex justify-center mb-5">
                  <div className="h-12 sm:h-16 w-auto relative flex items-center">
                    <img
                      src="/images/domelec-logo-new-official-640.webp"
                      alt="DomElec Logo"
                      className="h-full w-auto object-contain"
                    />
                  </div>
                </div>

                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  Reset Your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-400 dark:from-indigo-400 dark:to-indigo-300">
                    Password
                  </span>
                </h1>
                <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base">
                  Enter a new password to secure your DomElec Solar Manager account
                </p>
              </div>

              <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700/60 overflow-hidden">
                <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-indigo-900/20 dark:to-slate-800/50 border-b border-slate-200 dark:border-slate-700/60">
                  <div className="flex items-center">
                    <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-full mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 text-indigo-600 dark:text-indigo-400"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <p className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">
                      Create a strong password for enhanced security
                    </p>
                  </div>
                </div>

                <div className="p-5 sm:p-6">
                  <Form {...resetPasswordForm}>
                    <form
                      onSubmit={resetPasswordForm.handleSubmit(async (data) => {
                        try {
                          await resetPasswordMutation.mutateAsync({
                            token: resetToken,
                            newPassword: data.password,
                          });
                          // Show success state only after server confirms
                          setShowResetSuccess(true);
                          // Auto-redirect to login after 4 seconds
                          setTimeout(() => {
                            setLocation('/auth');
                          }, 4000);
                        } catch (error) {
                          console.error('Password reset failed:', error);
                        }
                      })}
                      className="space-y-4"
                    >
                      <FormField
                        control={resetPasswordForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                              New Password
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type="password"
                                  placeholder="Enter new password"
                                  className="pl-9"
                                  autoComplete="new-password"
                                  {...field}
                                />
                                <div className="absolute left-3 top-2.5 text-slate-400">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                  </svg>
                                </div>
                              </div>
                            </FormControl>
                            <FormDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              Minimum 6 characters, case sensitive
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={resetPasswordForm.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-slate-700 dark:text-slate-300 font-medium">
                              Confirm Password
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type="password"
                                  placeholder="Confirm your password"
                                  className="pl-9"
                                  autoComplete="new-password"
                                  {...field}
                                />
                                <div className="absolute left-3 top-2.5 text-slate-400">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                  </svg>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Security tips panel */}
                      <div className="mt-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700/60 text-sm text-slate-600 dark:text-slate-400">
                        <div className="flex items-center mb-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-2 text-indigo-500 dark:text-indigo-400"
                          >
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                            <path d="M12 8v4"></path>
                            <path d="M12 16h.01"></path>
                          </svg>
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            Password strength tips:
                          </span>
                        </div>
                        <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
                          <li>Use at least 8 characters</li>
                          <li>Include uppercase and lowercase letters</li>
                          <li>Add numbers and special characters</li>
                        </ul>
                      </div>

                      <Button
                        type="submit"
                        className="w-full mt-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-medium py-2 rounded-lg transition-all"
                        disabled={resetPasswordMutation.isPending}
                      >
                        {resetPasswordMutation.isPending ? (
                          <span className="flex items-center justify-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Resetting your password...
                          </span>
                        ) : (
                          'Reset Password'
                        )}
                      </Button>

                      {/* Processing indicator */}
                      {resetPasswordMutation.isPending && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg animate-in slide-in-from-top-2 duration-300">
                          <div className="flex items-center text-blue-700 dark:text-blue-300">
                            <svg
                              className="animate-spin mr-3 h-5 w-5 text-blue-600 dark:text-blue-400"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <div>
                              <div className="text-sm font-medium">
                                Processing your password reset...
                              </div>
                              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                Please wait while we securely update your password
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/60 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm"
                          onClick={() => {
                            setShowResetForm(false);
                            setResetToken(null);
                            setLocation('/auth');
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-1 h-4 w-4"
                          >
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                          </svg>
                          Back to Login
                        </Button>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>

              {/* Support section */}
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Need assistance?{' '}
                  <a
                    href="mailto:support@domelec.co.uk"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Contact our support team
                  </a>
                </p>
              </div>

              {/* Theme toggle */}
              <div className="mt-4 flex justify-center">
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password reset success view
  if (showResetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-emerald-100 to-white dark:from-emerald-950 dark:via-emerald-900 dark:to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <Card className="border-0 shadow-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl overflow-hidden">
            {/* Success header */}
            <div className="px-6 py-8 bg-gradient-to-r from-emerald-500 to-green-600 dark:from-emerald-600 dark:to-green-700 text-white text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-white"
                >
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
              </div>
              <h1 className="text-2xl font-bold mb-2">Password Reset Successful!</h1>
              <p className="text-emerald-100 text-sm">Your password has been updated securely</p>
            </div>

            {/* Success content */}
            <div className="p-6 text-center">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
                  What happens next?
                </h2>
                <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                      <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                        1
                      </span>
                    </div>
                    <span>Your new password is now active</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                      <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                        2
                      </span>
                    </div>
                    <span>You can now log in with your new credentials</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                      <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                        3
                      </span>
                    </div>
                    <span>Redirecting you to login in 4 seconds...</span>
                  </div>
                </div>
              </div>

              <Alert className="mb-6 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/50">
                <InfoCircledIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <AlertDescription className="text-emerald-700 dark:text-emerald-300 text-sm">
                  For your security, remember to keep your password private and consider enabling
                  two-factor authentication.
                </AlertDescription>
              </Alert>

              <Button
                onClick={() => setLocation('/auth')}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-medium py-2.5 rounded-lg transition-all shadow-lg"
              >
                Continue to Login
              </Button>
            </div>
          </Card>

          {/* Theme toggle */}
          <div className="mt-6 flex justify-center">
            <ThemeToggle />
          </div>
        </div>
      </div>
    );
  }

  // Email verification view
  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="text-center mb-6">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full w-16 h-16 mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-10 h-10 text-blue-600 dark:text-blue-400"
                >
                  <path d="M22 7L12 13 2 7"></path>
                  <path d="M2 7V17C2 18.1 2.9 19 4 19H20C21.1 19 22 18.1 22 17V7"></path>
                  <path d="M2 7L12 3L22 7"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Check Your Email
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-sm">
                We've sent a verification link to
              </p>
              <p className="text-slate-900 dark:text-white font-semibold">{verificationEmail}</p>
            </div>

            <div className="space-y-4">
              <Alert className="bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800">
                <InfoCircledIcon className="h-4 w-4" />
                <AlertDescription className="text-blue-800 dark:text-blue-300">
                  Click the verification link in your email to activate your account. The link will
                  expire in 24 hours.
                </AlertDescription>
              </Alert>

              {verificationError && (
                <Alert className="bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800">
                  <InfoCircledIcon className="h-4 w-4" />
                  <AlertDescription className="text-amber-800 dark:text-amber-300">
                    {verificationError.message}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={async () => {
                    setResendingVerification(true);
                    try {
                      await resendVerificationMutation.mutateAsync({ email: verificationEmail });
                    } catch (error) {
                      console.error('Failed to resend verification:', error);
                    } finally {
                      setResendingVerification(false);
                    }
                  }}
                  disabled={resendingVerification || resendVerificationMutation.isPending}
                  variant="outline"
                  className="w-full"
                >
                  {resendingVerification || resendVerificationMutation.isPending ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>

                <Button
                  onClick={() => {
                    setShowEmailVerification(false);
                    setVerificationError(null);
                  }}
                  variant="ghost"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>

              <div className="text-center text-xs text-slate-500 dark:text-slate-400">
                <p>Didn't receive the email? Check your spam folder or contact support.</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <ThemeToggle />
          </div>
        </div>
      </div>
    );
  }

  // Main auth page with login/register tabs
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-0">
      <div className="w-full relative">
        {/* Top wave design - decorative element */}
        <div className="absolute top-0 inset-x-0 h-16 md:h-20 lg:h-32 bg-indigo-600 dark:bg-indigo-700 opacity-60 dark:opacity-30 overflow-hidden">
          <svg
            className="absolute bottom-0 w-full text-white dark:text-slate-900"
            viewBox="0 0 1440 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M0 48H1440V0C1440 0 1082.89 40 720 40C357.11 40 0 0 0 0V48Z"
              fill="currentColor"
            />
          </svg>
        </div>

        {/* Main content container */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28 z-10 relative">
          <div className="grid lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {/* Auth forms - Displayed first on mobile */}
            <div className="order-first lg:col-span-3 lg:order-2 fade-in">
              <div className="w-full max-w-md mx-auto">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-white dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 mb-6 shadow-md">
                    <TabsTrigger
                      value="login"
                      className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-indigo-700 data-[state=active]:text-white data-[state=active]:shadow-sm py-2.5"
                    >
                      Login
                    </TabsTrigger>
                    <TabsTrigger
                      value="register"
                      className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-indigo-700 data-[state=active]:text-white data-[state=active]:shadow-sm py-2.5"
                    >
                      Register
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="slide-up">
                    <Card className="border-slate-200 dark:border-slate-700 shadow-lg bg-white dark:bg-slate-800">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-slate-900 dark:text-slate-100">
                          Welcome Back
                        </CardTitle>
                        <CardDescription className="text-slate-500 dark:text-slate-400">
                          Sign in to your account to continue
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Form {...loginForm}>
                          <form
                            onSubmit={loginForm.handleSubmit(async (data) => {
                              try {
                                await loginMutation.mutateAsync(data);
                              } catch (error: any) {
                                if (error.isVerificationError) {
                                  setVerificationError(error);
                                  setVerificationEmail(error.email);
                                  setShowEmailVerification(true);
                                }
                              }
                            })}
                            className="space-y-4"
                          >
                            <FormField
                              control={loginForm.control}
                              name="username"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-700 dark:text-slate-300">
                                    Email
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        type="email"
                                        placeholder="username@domelec.co.uk"
                                        className="pl-9"
                                        {...field}
                                      />
                                      <div className="absolute left-3 top-2.5 text-slate-400">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M22 7L12 13 2 7"></path>
                                          <path d="M2 7V17C2 18.1 2.9 19 4 19H20C21.1 19 22 18.1 22 17V7"></path>
                                          <path d="M2 7L12 3L22 7"></path>
                                        </svg>
                                      </div>
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={loginForm.control}
                              name="password"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-700 dark:text-slate-300">
                                    Password
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        type="password"
                                        className="pl-9"
                                        placeholder="••••••••"
                                        {...field}
                                      />
                                      <div className="absolute left-3 top-2.5 text-slate-400">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <rect
                                            x="3"
                                            y="11"
                                            width="18"
                                            height="11"
                                            rx="2"
                                            ry="2"
                                          ></rect>
                                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                      </div>
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <Button
                              type="submit"
                              variant="domelec-blue"
                              className="w-full"
                              disabled={loginMutation.isPending}
                            >
                              {loginMutation.isPending ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Signing In...
                                </span>
                              ) : (
                                'Sign In'
                              )}
                            </Button>
                            {loginMutation.isError && (
                              <p className="text-sm text-red-500 mt-2">Invalid email or password</p>
                            )}
                          </form>
                        </Form>
                      </CardContent>
                      <CardFooter className="pt-0 flex flex-col space-y-4">
                        <div className="w-full border-t border-slate-200 dark:border-slate-700 pt-4 text-center">
                          <Button
                            type="button"
                            variant="link"
                            className="text-sm text-indigo-600 dark:text-indigo-400 p-0 h-auto"
                            onClick={() => setShowForgotPasswordForm(true)}
                          >
                            Forgot your password?
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  </TabsContent>

                  <TabsContent value="register" className="slide-up">
                    <Card className="border-slate-200 dark:border-slate-700 shadow-lg bg-white dark:bg-slate-800">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-slate-900 dark:text-slate-100">
                          Create Account
                        </CardTitle>
                        <CardDescription className="text-slate-500 dark:text-slate-400">
                          Register to access DomElec Solar Manager
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Alert className="mb-4 bg-blue-50 dark:bg-slate-800 border-blue-200 dark:border-blue-900 text-blue-800 dark:text-blue-300">
                          <InfoCircledIcon className="h-4 w-4 mr-2" />
                          <AlertDescription>
                            Registration is only available for @domelec.co.uk email addresses
                          </AlertDescription>
                        </Alert>

                        <Form {...registerForm}>
                          <form
                            onSubmit={registerForm.handleSubmit(async (data) => {
                              try {
                                const response = await registerMutation.mutateAsync(data);
                                if (response.emailSent) {
                                  setVerificationEmail(data.username);
                                  setShowEmailVerification(true);
                                }
                              } catch (error) {
                                console.error('Registration failed:', error);
                              }
                            })}
                            className="space-y-4"
                          >
                            <FormField
                              control={registerForm.control}
                              name="username"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-700 dark:text-slate-300">
                                    Email
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        type="email"
                                        placeholder="username@domelec.co.uk"
                                        className="pl-9"
                                        {...field}
                                      />
                                      <div className="absolute left-3 top-2.5 text-slate-400">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M22 7L12 13 2 7"></path>
                                          <path d="M2 7V17C2 18.1 2.9 19 4 19H20C21.1 19 22 18.1 22 17V7"></path>
                                          <path d="M2 7L12 3L22 7"></path>
                                        </svg>
                                      </div>
                                    </div>
                                  </FormControl>
                                  <FormDescription className="text-xs text-slate-500 dark:text-slate-400">
                                    Must be a valid domelec.co.uk email address
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={registerForm.control}
                              name="password"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-700 dark:text-slate-300">
                                    Password
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        type="password"
                                        className="pl-9"
                                        placeholder="••••••••"
                                        {...field}
                                      />
                                      <div className="absolute left-3 top-2.5 text-slate-400">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <rect
                                            x="3"
                                            y="11"
                                            width="18"
                                            height="11"
                                            rx="2"
                                            ry="2"
                                          ></rect>
                                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                      </div>
                                    </div>
                                  </FormControl>
                                  <FormDescription className="text-xs text-slate-500 dark:text-slate-400">
                                    At least 6 characters
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Security notice */}
                            <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-200 dark:border-slate-700/50 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                              <p>
                                By registering, you agree to DomElec's{' '}
                                <a
                                  href="#"
                                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                  Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                  href="#"
                                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                  Privacy Policy
                                </a>
                                .
                              </p>
                            </div>

                            <Button
                              type="submit"
                              variant="domelec-blue"
                              className="w-full"
                              disabled={registerMutation.isPending}
                            >
                              {registerMutation.isPending ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Creating Account...
                                </span>
                              ) : (
                                'Create Account'
                              )}
                            </Button>
                            {registerMutation.isError && (
                              <p className="text-sm text-red-500 mt-2">
                                Registration failed: This email may already be in use
                              </p>
                            )}
                          </form>
                        </Form>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* Left side - Brand message panel - Shown second on mobile */}
            <div className="order-last lg:order-1 lg:col-span-2 xl:pr-12 fade-in mt-8 lg:mt-0">
              <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-100 dark:border-slate-700/60">
                <div className="mb-6">
                  {/* Logo container */}
                  <div className="flex justify-center lg:justify-start mb-5">
                    <div className="h-16 w-auto relative flex items-center">
                      <img
                        src="/images/domelec-logo-new-official-640.webp"
                        alt="DomElec Logo"
                        className="h-full w-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Product name and description */}
                  <div className="text-center lg:text-left">
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                      DomElec{' '}
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-400 dark:from-indigo-400 dark:to-indigo-300">
                        Solar Manager
                      </span>
                    </h1>
                    <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base">
                      Comprehensive solar proposal and installation management
                    </p>
                  </div>
                </div>

                {/* Feature highlights - same for all screens */}
                <div className="space-y-4 mt-8">
                  <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 flex items-start transition-all">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 w-10 h-10 rounded-full flex items-center justify-center mr-4 shrink-0 shadow-md">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 text-white"
                      >
                        <circle cx="12" cy="12" r="5" />
                        <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                        Solar Proposals
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Create detailed proposals for solar installations with financial
                        projections.
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 flex items-start transition-all">
                    <div className="bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 w-10 h-10 rounded-full flex items-center justify-center mr-4 shrink-0 shadow-md">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 text-white"
                      >
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                        <path d="M12 11h4" />
                        <path d="M12 16h4" />
                        <path d="M8 11h.01" />
                        <path d="M8 16h.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                        Digital Authorization
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Streamline LOA submissions with digital signatures and automated tracking.
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 flex items-start transition-all">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 w-10 h-10 rounded-full flex items-center justify-center mr-4 shrink-0 shadow-md">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5 text-white"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                        Installation Tracker
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Monitor every stage of installation with milestone tracking and
                        notifications.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Support or additional information - visible on all screens */}
                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      Need help?{' '}
                      <a
                        href="mailto:support@domelec.co.uk"
                        className="text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Contact support
                      </a>
                    </div>
                    <div>
                      <ThemeToggle />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgotPasswordForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 relative animate-fadeIn">
            <Button
              onClick={() => setShowForgotPasswordForm(false)}
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 h-8 w-8"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </Button>

            <div className="text-center mb-6">
              <div className="inline-block p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-indigo-600 dark:text-indigo-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                Reset Your Password
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                Enter your email to receive a password reset link
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    className="w-full px-4 py-2 pl-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-600 focus:border-transparent"
                    placeholder="Enter your email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  />
                  <div className="absolute left-3 top-2.5 text-slate-400">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 7L12 13 2 7"></path>
                      <path d="M2 7V17C2 18.1 2.9 19 4 19H20C21.1 19 22 18.1 22 17V7"></path>
                      <path d="M2 7L12 3L22 7"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleForgotPassword}
                className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white"
                disabled={sendingForgotPasswordEmail}
              >
                {sendingForgotPasswordEmail ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending Email...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <div className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
                Remember your password?{' '}
                <Button
                  onClick={() => setShowForgotPasswordForm(false)}
                  variant="link"
                  className="text-indigo-600 dark:text-indigo-400 font-medium p-0 h-auto"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Success Modal */}
      {showPasswordResetSuccess && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-8 text-center relative animate-fadeIn">
            <div className="mb-6">
              <div className="inline-block p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-emerald-600 dark:text-emerald-400"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                Password Reset Email Sent
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
              </p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium text-slate-900 dark:text-white mb-2 text-sm">
                What happens next:
              </h3>
              <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                <li>• Check your email inbox (and spam folder)</li>
                <li>• Click the reset link in the email</li>
                <li>• Create a new secure password</li>
                <li>• Sign in with your new password</li>
              </ul>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-6">
              <div className="flex items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-amber-600 dark:text-amber-400 mt-0.5 mr-2 shrink-0"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  For security, the reset link will expire in 1 hour. Keep your new password safe
                  and don't share it with anyone.
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowPasswordResetSuccess(false)}
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-medium"
            >
              Continue to Login
            </Button>

            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">
              Automatically redirecting in <span id="countdown">4</span> seconds...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
